import { useState } from 'react';
import {
  useUsers,
  useDeleteUser,
  useResendSetupLink,
} from '@/hooks/useAdmin';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/lib/utils';
import { SkeletonTable } from '@/components/skeletons/SkeletonLoaders';
import { ErrorState } from '@/components/EmptyState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { UserModal } from '@/components/modals/UserModal';
import { ApiKeyManagementModal } from '@/components/modals/ApiKeyManagementModal';
import { useToast } from '@/hooks/useToast';
import { Users, UserPlus, Edit, Trash2, Shield, CheckCircle2, XCircle, Bot, Key, Mail, Clock } from 'lucide-react';
import type { User } from '@/types';

// A human user who has not yet completed magic-link setup (no password set).
// API-only accounts and verified/legacy humans are excluded.
const isPendingSetup = (user: User): boolean =>
  user.user_type !== 'api_only' && user.is_verified === false;

export const UserManagement = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);
  const [apiKeyUser, setApiKeyUser] = useState<User | null>(null);
  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const [userToResend, setUserToResend] = useState<User | null>(null);

  const { data, isLoading, error, refetch } = useUsers({
    page,
    size: 20,
    search: search || undefined,
  });

  const deleteUser = useDeleteUser();
  const resendSetupLink = useResendSetupLink();
  const { toast } = useToast();

  const users = data?.items || [];
  const totalPages = data?.pages || 1;

  const handleSearch = () => {
    setSearch(searchInput.trim());
    setPage(1);
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const handleManageKeys = (user: User) => {
    setApiKeyUser(user);
    setApiKeyModalOpen(true);
  };

  const handleDeleteClick = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleResendClick = (user: User) => {
    setUserToResend(user);
    setResendDialogOpen(true);
  };

  const handleResendConfirm = async () => {
    if (!userToResend) return;

    try {
      await resendSetupLink.mutateAsync(userToResend.id);
      toast({
        title: 'Setup link sent',
        description: `A new setup link has been emailed to ${userToResend.email}. Any previously sent link no longer works.`,
      });
      setResendDialogOpen(false);
      setUserToResend(null);
    } catch (err: unknown) {
      const error = err as Error;
      toast({
        title: 'Error',
        description: error.message || 'Failed to resend setup link',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      await deleteUser.mutateAsync(userToDelete.id);
      toast({
        title: 'Success',
        description: `User ${userToDelete.email} has been deactivated${
          userToDelete.user_type === 'api_only' ? '. All associated API keys have been revoked.' : ''
        }`,
      });
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (err: unknown) {
      const error = err as Error;
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete user',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">User Management</h1>
          <p className="text-muted-foreground">
            Manage user accounts and permissions
          </p>
        </div>
        <Button onClick={handleCreateUser}>
          <UserPlus className="h-4 w-4 mr-2" />
          Create User
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle>Search Users</CardTitle>
          <CardDescription>
            Search by email address or name
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              placeholder="Search users..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="flex-1"
            />
            <Button onClick={handleSearch}>Search</Button>
            {search && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearch('');
                  setSearchInput('');
                  setPage(1);
                }}
              >
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Users
          </CardTitle>
          <CardDescription>
            {data?.total || 0} user{(data?.total || 0) !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonTable rows={5} />
          ) : error ? (
            <ErrorState
              message={error instanceof Error ? error.message : 'Failed to load users'}
              onRetry={() => refetch()}
            />
          ) : users.length === 0 ? (
            <div className="text-center text-muted-foreground py-12">
              {search ? 'No users match your search' : 'No users found'}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-medium">Email</th>
                      <th className="px-4 py-2 text-left text-sm font-medium">Name</th>
                      <th className="px-4 py-2 text-left text-sm font-medium">Status</th>
                      <th className="px-4 py-2 text-left text-sm font-medium">Permissions</th>
                      <th className="px-4 py-2 text-left text-sm font-medium">Created</th>
                      <th className="px-4 py-2 text-left text-sm font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {users.map((user) => {
                      const isApiOnly = user.user_type === 'api_only';
                      return (
                        <tr key={user.id} className="hover:bg-muted/50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {isApiOnly && (
                                <Bot className="h-4 w-4 text-info" aria-label="API-Only Service Account" />
                              )}
                              <span className="font-medium">{user.email}</span>
                            </div>
                            {isApiOnly && (
                              <div className="mt-1">
                                <Badge variant="outline" className="text-xs bg-info/10 text-info border-info">
                                  Service Account
                                </Badge>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {user.full_name || (
                              <span className="text-muted-foreground italic">Not set</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <Badge
                                variant={user.is_active ? 'default' : 'secondary'}
                                className={
                                  user.is_active
                                    ? 'bg-success/10 text-success hover:bg-success/10'
                                    : ''
                                }
                              >
                                {user.is_active ? (
                                  <>
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    Active
                                  </>
                                ) : (
                                  <>
                                    <XCircle className="h-3 w-3 mr-1" />
                                    Inactive
                                  </>
                                )}
                              </Badge>
                              {user.is_verified && !isApiOnly && (
                                <Badge variant="outline">Verified</Badge>
                              )}
                              {isPendingSetup(user) && (
                                <Badge
                                  variant="outline"
                                  className="bg-warning/10 text-warning border-warning/30"
                                >
                                  <Clock className="h-3 w-3 mr-1" />
                                  Pending setup
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              {user.is_superuser && (
                                <Badge
                                  variant="destructive"
                                  className="bg-violet/10 text-violet hover:bg-violet/10"
                                >
                                  <Shield className="h-3 w-3 mr-1" />
                                  Superuser
                                </Badge>
                              )}
                              {isApiOnly && (
                                <Badge
                                  variant="outline"
                                  className="bg-info/10 text-info border-info"
                                >
                                  <Key className="h-3 w-3 mr-1" />
                                  API Key
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {formatDate(user.created_at)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditUser(user)}
                              >
                                <Edit className="h-4 w-4 mr-1" />
                                Edit
                              </Button>
                              {isApiOnly && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleManageKeys(user)}
                                  className="bg-info/10 hover:bg-info/20 text-info border-info"
                                >
                                  <Key className="h-4 w-4 mr-1" />
                                  Manage Keys
                                </Button>
                              )}
                              {isPendingSetup(user) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleResendClick(user)}
                                  disabled={resendSetupLink.isPending}
                                  className="bg-warning/10 hover:bg-warning/20 text-warning border-warning/30"
                                >
                                  <Mail className="h-4 w-4 mr-1" />
                                  Resend setup link
                                </Button>
                              )}
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDeleteClick(user)}
                                disabled={deleteUser.isPending}
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* User Modal */}
      <UserModal
        open={userModalOpen}
        onOpenChange={setUserModalOpen}
        user={selectedUser}
      />

      {/* API Key Management Modal */}
      <ApiKeyManagementModal
        open={apiKeyModalOpen}
        onOpenChange={setApiKeyModalOpen}
        user={apiKeyUser}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete User"
        description={
          userToDelete
            ? `Are you sure you want to deactivate ${userToDelete.email}? ${
                userToDelete.user_type === 'api_only'
                  ? 'This will revoke all associated API keys and immediately stop all applications using these keys from accessing the API.'
                  : 'This will prevent them from logging in.'
              }`
            : ''
        }
        onConfirm={handleDeleteConfirm}
        confirmText="Delete"
        variant="destructive"
        loading={deleteUser.isPending}
      />

      {/* Resend Setup Link Confirmation */}
      <ConfirmDialog
        open={resendDialogOpen}
        onOpenChange={setResendDialogOpen}
        title="Resend setup link"
        description={
          userToResend
            ? `Resend the account setup link to ${userToResend.email}? Any previously sent link will stop working.`
            : ''
        }
        onConfirm={handleResendConfirm}
        confirmText="Resend"
        loading={resendSetupLink.isPending}
      />
    </div>
  );
};
